import React, { useContext, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import CustomModal from '../../../components/CustomModal/CustomModal';
import CustomInput from '../../../components/CustomInput/CustomInput';
import Button from '../../../components/Button/Button';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';
import { withDefaultScheme } from '../../../utils/linkUrlUtils';
import WebsiteSourceService from '../../../services/WebsiteSourceService/WebsiteSourceService';
import {
  SCHEDULE_PRESETS,
  WebsiteSourceRequest,
  WebsiteSourceResponse
} from '../../../types/WebsiteSource';
import ExclusionEditor, {
  EditableExclusion,
  toEditableExclusion
} from './ExclusionEditor';

interface WebsiteSourceDialogProps {
  isOpen: boolean;
  source: WebsiteSourceResponse | null;
  onClose: () => void;
  onSaved: (source: WebsiteSourceResponse) => void;
}

const CUSTOM = 'custom';

const DOCUMENT_PATH = /\.(pdf|docx?)$/i;

const isDocumentUrl = (address: string): boolean => {
  try {
    const { pathname } = new URL(withDefaultScheme(address.trim()));
    return DOCUMENT_PATH.test(pathname);
  } catch {
    return false;
  }
};

const presetFor = (hours: number): string =>
  SCHEDULE_PRESETS.some((preset) => preset.hours === hours)
    ? hours.toString()
    : CUSTOM;

export const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || fallback;

function WebsiteSourceDialog({
  isOpen,
  source,
  onClose,
  onSaved
}: Readonly<WebsiteSourceDialogProps>): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const [seedUrl, setSeedUrl] = useState('');
  const [preset, setPreset] = useState('24');
  const [customHours, setCustomHours] = useState('24');
  const [maxPages, setMaxPages] = useState('500');
  const [followDocuments, setFollowDocuments] = useState(false);
  const [exclusions, setExclusions] = useState<EditableExclusion[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (source) {
      setSeedUrl(source.seedUrl);
      setPreset(presetFor(source.intervalHours));
      setCustomHours(source.intervalHours.toString());
      setMaxPages(source.maxPages.toString());
      setFollowDocuments(source.followDocuments);
      setExclusions(source.rules.map(toEditableExclusion));
    } else {
      setSeedUrl('');
      setPreset('24');
      setCustomHours('24');
      setMaxPages('500');
      setFollowDocuments(false);
      setExclusions([]);
    }
  }, [isOpen, source]);

  const seedIsDocument = isDocumentUrl(seedUrl);
  const includeDocuments = followDocuments || seedIsDocument;

  const intervalHours = (): number =>
    preset === CUSTOM ? Number(customHours) : Number(preset);

  const localValidation = (): string | null => {
    if (!seedUrl.trim()) return 'Enter the website address to crawl.';
    const hours = intervalHours();
    if (!Number.isInteger(hours) || hours < 1 || hours > 8760) {
      return 'The schedule must be between 1 and 8760 hours.';
    }
    const pages = Number(maxPages);
    if (!Number.isInteger(pages) || pages < 1 || pages > 5000) {
      return 'Max pages must be between 1 and 5000.';
    }
    if (exclusions.some((exclusion) => !exclusion.value.trim())) {
      return 'Every exclusion needs a value.';
    }
    return null;
  };

  const buildRequest = (): WebsiteSourceRequest => ({
    seedUrl: seedUrl.trim(),
    intervalHours: intervalHours(),
    maxPages: Number(maxPages),
    followDocuments: includeDocuments,
    rules: exclusions.map(({ scope, value }) => ({
      scope,
      value: value.trim()
    })),
    ...(source && { version: source.version })
  });

  const handleSave = async (): Promise<void> => {
    if (!currentChatbot) return;
    const problem = localValidation();
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const request = buildRequest();
      const saved = source
        ? await WebsiteSourceService.update(
            currentChatbot.id,
            source.id,
            request
          )
        : await WebsiteSourceService.create(currentChatbot.id, request);
      onSaved(saved);
      onClose();
    } catch (err: any) {
      setError(
        apiErrorMessage(err, 'Unable to save the website. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomModal
      open={isOpen}
      onClose={onClose}
      title={source ? 'Edit website' : 'Add a website'}
      description='Pages from this site are crawled on a schedule into the knowledge base. Saving schedules a crawl within a minute.'
      maxWidth='3xl'
    >
      <div className='flex flex-col gap-4'>
        <CustomInput
          label='Website address'
          type='url'
          value={seedUrl}
          placeholder='https://www.example.com or https://www.example.com/services/'
          helperText='A root address crawls the whole site. An address with a path crawls only that path and the pages under it.'
          onChange={(e) => setSeedUrl(e.target.value)}
          fullWidth
        />

        <div className='flex flex-wrap items-end gap-4'>
          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Schedule</span>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className='w-[160px]' aria-label='Schedule'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEDULE_PRESETS.map((item) => (
                  <SelectItem key={item.hours} value={item.hours.toString()}>
                    {item.label}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM}>Custom (hours)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {preset === CUSTOM && (
            <CustomInput
              label='Every N hours'
              type='number'
              min={1}
              max={8760}
              value={customHours}
              onChange={(e) => setCustomHours(e.target.value)}
            />
          )}
          <CustomInput
            label='Max pages per crawl'
            type='number'
            min={1}
            max={5000}
            value={maxPages}
            onChange={(e) => setMaxPages(e.target.value)}
          />
        </div>

        <div className='flex items-center gap-2 text-sm'>
          <Switch
            checked={includeDocuments}
            onCheckedChange={setFollowDocuments}
            disabled={seedIsDocument}
            aria-labelledby='website-follow-documents-label'
          />
          <span id='website-follow-documents-label'>
            Include linked PDF and Word documents
          </span>
        </div>

        <ExclusionEditor exclusions={exclusions} onChange={setExclusions} />

        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        <div className='flex justify-end gap-2'>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type='button' onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {source ? 'Save' : 'Add website'}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}

export default WebsiteSourceDialog;
